/**
 * The small part of an app's project catalog that can change the result of
 * `mod.type.list()`.
 *
 * A project document changes for sessions, comments, tasks, and every other
 * app too. Waking an installed app for each of those changes turns one busy
 * project into an unbounded cross-frame refresh loop. Keep that boundary
 * explicit: an app needs a catalog wake only when one of ITS records appears,
 * disappears, or is renamed.
 */

import type { DocumentRef } from '@mod/core/model'

export function appViewInstancesKey(records: DocumentRef[]): string {
  return records
    .map(({ id, title, path }) => [id, title, path] as const)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([id, title, path]) => `${id}\u0000${title}\u0000${path}`)
    .join('\u0001')
}

/**
 * The comments on an app's own records, as a key that changes when one is
 * written, answered, edited or resolved.
 *
 * Comments live on the PROJECT, not on the app's records, so without this a
 * comment written anywhere else (the side column, an agent) never woke the
 * app, and a review showed a reply only after a reload. Scoped to the app's
 * records, so comments on other documents still do not wake it.
 */
export function appCommentsKey(
  comments: Record<string, unknown> | undefined,
  recordIds: ReadonlySet<string>,
): string {
  const parts: string[] = []
  for (const [id, raw] of Object.entries(comments ?? {})) {
    const c = raw as {
      target?: { documentId?: unknown }
      updatedAt?: unknown
      resolved?: unknown
    }
    const doc = c?.target?.documentId
    if (doc === undefined || !recordIds.has(String(doc))) continue
    parts.push(
      `${id}\u0000${String(c.updatedAt ?? '')}\u0000${c.resolved ? 1 : 0}`,
    )
  }
  return parts.sort().join('\u0001')
}
